import { memo, useMemo, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { ChatAttachment, FinalizedAttachment, PendingAttachment } from '../../../types'
import { AudioNote } from './nested/audio-note-attachment'
import { Clipboard } from './nested/clipboard-attachment'
import { File } from './nested/file-attachment'
import { Image, WindowContents } from './nested/image-attachment'
import { InsightAttachments } from './nested/insight-attachments'
import { LocalPathAttachment } from './nested/local-path-attachment'
import { QuoteReference } from './nested/quote-reference-attachment'
import { SelectedText } from './nested/selected-text-attachment'
import { SkillAttachment } from './nested/skill-attachment'
import { StagedActionAttachment } from './nested/staged-action-attachment'
import { SummaryAttachment } from './nested/summary-attachment'
import type { AttachmentPillProps } from './types'

export { AttachmentStub } from './attachment-stub'

// Opacity only, deliberately: the overlay window hides/shows constantly, and a
// transform/filter animation stranded mid-flight (paused rAF, module swap)
// leaves pills permanently squished or blurry. Identity-based keys (below) are
// what prevent one pill "morphing" into another — a replaced attachment
// remounts and crossfades instead of swapping content in a reused pill.
export const attachmentAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, transition: { duration: 0.15, ease: 'easeOut' } },
  transition: { duration: 0.2, ease: 'easeOut' },
} as const

type AttachmentKind = FinalizedAttachment['type'] | PendingAttachment['type']

/** What a pill can't know from its own attachment: insights render as a set, and a window capture pairs with a screenshot. */
type ListContext = {
  attachments: ChatAttachment[]
  insights: ChatAttachment[]
  hasImage: boolean
}

/**
 * Picks the component for one attachment. A plain function rather than a
 * component so the rendered tree stays exactly as deep as the pill itself.
 */
function renderPill(pill: AttachmentPillProps, kind: AttachmentKind | undefined, list: ListContext): ReactNode {
  switch (kind) {
    case 'audioNote':
      return <AudioNote {...pill} />
    case 'clipboardText':
      return <Clipboard {...pill} />
    case 'selectedText':
      return <SelectedText {...pill} />
    case 'image': {
      const image = pill.attachment as Extract<FinalizedAttachment, { type: 'image' }> | undefined
      const windowContents = image
        ? (list.attachments.find((a) => a.attachment?.type === 'windowContents' && a.attachment.name === image.fileName)
            ?.attachment as Extract<FinalizedAttachment, { type: 'windowContents' }> | undefined)
        : undefined

      return image && windowContents ? (
        <WindowContents image={image} windowContents={windowContents} canRemove={pill.canRemove} />
      ) : (
        <Image {...pill} />
      )
    }
    case 'windowContents': {
      // Without a screenshot of its own, the capture stands in with its app
      // icon. Alongside one, the image case already renders the pair.
      const wc = pill.attachment as Extract<FinalizedAttachment, { type: 'windowContents' }> | undefined
      if (!wc?.appIcon || list.hasImage) return null
      return (
        <WindowContents
          image={{ type: 'image', fileDataUrl: wc.appIcon }}
          windowContents={wc}
          canRemove={pill.canRemove}
        />
      )
    }
    case 'file': // pending state
    case 'textFile':
    case 'spreadsheet':
    case 'pdf':
      return <File {...pill} />
    case 'quoteReference':
      return <QuoteReference {...pill} />
    case 'dailySummary':
      return <SummaryAttachment {...pill} />
    case 'insight':
      return <InsightAttachments insights={list.insights} canRemove={pill.canRemove} />
    case 'skill':
      return <SkillAttachment {...pill} />
    case 'stagedAction':
      return <StagedActionAttachment {...pill} />
    case 'localPath':
      return <LocalPathAttachment {...pill} />
    default:
      return null
  }
}

export const Attachments = memo(
  ({
    messageId,
    conversationId,
    attachments,
    canRemove,
  }: {
    messageId?: string
    conversationId?: string
    attachments: ChatAttachment[]
    canRemove?: boolean
  }) => {
    const insights = useMemo(() => {
      return attachments.filter((a) => a.attachment?.type === 'insight')
    }, [attachments])

    const indexOfFirstInsight = useMemo(() => {
      return attachments.findIndex((a) => a.attachment?.type === 'insight')
    }, [attachments])

    const hasImage = useMemo(() => {
      return attachments.some((a) => a.attachment?.type === 'image')
    }, [attachments])

    return (
      <>
        <AnimatePresence mode="popLayout">
          {attachments.map((attachment, index) => {
            const kind = attachment.pending?.type ?? attachment.attachment?.type

            // Every insight collapses into the first one's pill.
            if (kind === 'insight' && indexOfFirstInsight !== index) {
              return null
            }

            const pill = renderPill({ ...attachment, index, canRemove, conversationId }, kind, {
              attachments,
              insights,
              hasImage,
            })
            if (!pill) return null

            const attachmentsMatchingKind = attachments.filter(
              (a) => a.attachment?.type === kind || a.pending?.type === kind,
            )
            const indexOfAttachmentByKind = attachmentsMatchingKind.indexOf(attachment)
            // Key on the attachment's identity where one exists — an index-based
            // key makes React reuse the pill's DOM when one attachment replaces
            // another of the same type, silently swapping content ("morphing")
            // instead of playing the exit/enter animation.
            const identity =
              (attachment.pending?.type === 'stagedAction' ? attachment.pending.actionId : undefined) ??
              (attachment.metadata as { id?: string } | undefined)?.id ??
              indexOfAttachmentByKind
            // The collapsed insight pill shows a count, so it remounts when the set grows.
            const key =
              kind === 'insight'
                ? `${messageId}-${kind}-attachment-${indexOfAttachmentByKind}-${insights.length}`
                : `${messageId}-${kind}-attachment-${identity}`

            return (
              <motion.div key={key} {...attachmentAnimation}>
                {pill}
              </motion.div>
            )
          })}
        </AnimatePresence>
      </>
    )
  },
)
Attachments.displayName = 'Attachments'
